import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAPI } from '../../../utils/utils';
import { buildTemporaryCastellerInfo, createTemporaryRequestId, requestNewPerson } from './newPersonUtils';

const sameBoard = (a, b) => ['selectedEvent', 'selectedBundle', 'selectedVersio']
    .every(key => String(a[key]) === String(b[key]));
let nextLocalId = -2;

const requestTemporaryAction = (socket, event, payload, done) => {
    let completed = false;
    const timer = setTimeout(() => finish({ success: false, error: 'No s’ha rebut confirmació. Pots tornar-ho a provar sense duplicar el canvi.' }), 15000);
    const finish = result => {
        if (completed) return;
        completed = true;
        clearTimeout(timer);
        done(result || { success: false });
    };
    if (socket.connected === false) finish({ success: false, error: 'No hi ha connexió. Torna-ho a provar quan es recuperi.' });
    else socket.emit(event, payload, finish);
};

// One instance lives at board level, not in each person row: requests and retry
// keys survive search changes, filtering and the creation row disappearing.
export default function useTemporaryPerson(props) {
    const current = useRef(props);
    const pending = useRef(false);
    const creationAttempts = useRef(new Map());
    const mounted = useRef(true);
    const [notice, setNotice] = useState(null);
    const dismissNotice = useCallback(() => setNotice(null), []);
    useEffect(() => setNotice(null), [props.selectedEvent, props.selectedBundle, props.selectedVersio]);
    current.current = props;
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    const visible = target => mounted.current && sameBoard(target, current.current);
    const show = value => { if (mounted.current) setNotice(value); };
    const refresh = target => {
        if (!visible(target)) return;
        current.current.refreshCastellersInfo?.();
        fetchAPI(`/all_assistencies_event/${target.selectedEvent}`, data => {
            if (visible(target)) current.current.setAssistenciesEvent?.(rows => [...(data.data || []), ...(rows || []).filter(person => person.id < -1)]);
        }, false);
    };
    const reload = target => {
        if (visible(target)) target.socket.emit('.load_positions', target.selectedEvent, target.selectedBundle, target.selectedVersio);
    };
    const requestFor = (target, personId) => ({
        room: `${target.selectedEvent}.${target.selectedBundle}.${target.selectedVersio}`,
        box: String(target.caixaSelected), personId, requestId: createTemporaryRequestId(),
    });
    const preview = (target, request, personId) => {
        if (!visible(target)) return;
        current.current.setPosicionsLog(log => [...(log || []), `${request.box},${personId},local,,optimistic-${request.requestId}`]);
    };
    const settle = (target, request, personId, actionId) => {
        if (!visible(target)) return;
        current.current.setPosicionsLog(log => {
            const received = log?.some(line => line.split(',')[4] === actionId);
            return (log || []).flatMap(line => line.split(',')[4] !== `optimistic-${request.requestId}` ? [line]
                : personId !== undefined && !received ? [`${request.box},${personId},local,,${actionId}`] : []);
        });
    };

    const createTemporaryPerson = name => {
        const target = current.current;
        const displayName = name?.trim();
        if (!displayName || pending.current) return false;
        if (target.caixaSelected === -1 || target.posicions?.loading) {
            show({ message: 'Selecciona una caixa per afegir-hi la persona temporal.', actions: [] });
            return false;
        }
        const attemptKey = JSON.stringify([target.selectedEvent, target.selectedBundle, target.selectedVersio, target.caixaSelected, displayName]);
        if (creationAttempts.current.has(attemptKey)) {
            return creationAttempts.current.get(attemptKey)();
        }
        const request = requestFor(target, null);
        request.expectedPersonId = target.posicions?.caixes?.[request.box] ?? null;
        const localId = nextLocalId--;
        const localPerson = buildTemporaryCastellerInfo(localId, displayName);
        const cleanup = personId => {
            if (!visible(target)) return;
            current.current.setCastellersInfo(info => {
                const next = { ...info };
                delete next[localId];
                if (personId) next[personId] = next[personId] || buildTemporaryCastellerInfo(personId, displayName);
                return next;
            });
            current.current.setAssistenciesEvent?.(rows => {
                const next = (rows || []).filter(row => row.id !== localId);
                if (personId && !next.some(row => Number(row.id) === Number(personId))) {
                    next.push(buildTemporaryCastellerInfo(personId, displayName));
                }
                return next;
            });
        };
        const attempt = () => {
            if (pending.current) return false;
            pending.current = true;
            show(null);
            if (visible(target)) {
                const previewId = request.personId || localId;
                const person = request.personId ? buildTemporaryCastellerInfo(previewId, displayName) : localPerson;
                current.current.setCastellersInfo(info => ({ ...info, [previewId]: info[previewId] || person }));
                current.current.setAssistenciesEvent?.(rows => (rows || []).some(row => Number(row.id) === Number(previewId))
                    ? rows : [...(rows || []), person]);
                preview(target, request, previewId);
            }
            const fail = response => {
                pending.current = false;
                settle(target, request);
                cleanup(request.personId);
                refresh(target);
                const occupied = response.reason === 'occupied';
                if (occupied) reload(target);
                const occupant = current.current.castellersInfo?.[response.occupantId]?.mote || 'una altra persona';
                show({
                    confirmation: occupied,
                    message: occupied ? `La caixa ha canviat i ara hi ha ${response.occupantId ? occupant : 'una posició buida'}. Vols substituir-la?`
                        : response.error || 'No s’ha pogut desar la persona a la pinya.',
                    actions: [{ label: occupied ? 'Substituir i col·locar' : 'Tornar-ho a provar', run: () => {
                        if (occupied) request.expectedPersonId = response.occupantId;
                        attempt();
                    } }],
                });
            };
            const place = () => requestTemporaryAction(target.socket, '.place_temporary_person', request, response => {
                if (!response.success) return fail(response);
                pending.current = false;
                creationAttempts.current.delete(attemptKey);
                cleanup(request.personId);
                settle(target, request, request.personId, `temporary-place-${request.requestId}`);
                if (response.replayed) reload(target);
                refresh(target);
            });
            if (request.personId) place();
            else requestNewPerson({ socket: target.socket, mote: displayName, eventId: target.selectedEvent,
                provaId: target.selectedBundle, requestId: request.requestId,
                onError: error => fail({ error }),
                onSuccess: ({ newPersonId }) => {
                    request.personId = newPersonId;
                    if (visible(target)) {
                        // Register the real identity before placement can be broadcast.
                        // Promote the optimistic entry in place, preserving later board edits.
                        cleanup(newPersonId);
                        current.current.setPosicionsLog(log => (log || []).map(line =>
                            line.split(',')[4] === `optimistic-${request.requestId}`
                                ? `${request.box},${newPersonId},local,,optimistic-${request.requestId}` : line));
                    }
                    place();
                },
            });
            // The local placement has started; the caller can finish its search now.
            return true;
        };
        creationAttempts.current.set(attemptKey, attempt);
        return attempt();
    };

    const removeTemporaryPerson = personId => {
        if (pending.current || Number(personId) < 1) return;
        const target = current.current;
        const request = requestFor(target, personId);
        const personName = target.castellersInfo?.[personId]?.mote || 'Persona temporal';
        const attempt = () => {
            if (pending.current) return;
            pending.current = true;
            show(null);
            // First check usage without a remove/reinsert flicker. Once the
            // scope is explicitly chosen, it is safe to preview the removal.
            if (request.scope) preview(target, request, '_EMPTY_');
            requestTemporaryAction(target.socket, '.remove_temporary_person', request, response => {
                pending.current = false;
                if (response.success) {
                    settle(target, request, '_EMPTY_', `temporary-remove-${personId}-${request.requestId}`);
                    if (response.replayed) reload(target);
                    refresh(target);
                    return;
                }
                settle(target, request);
                if (response.reason === 'choose_scope') {
                    if (!visible(target)) return;
                    const otherProves = response.count === 2 ? 'una altra prova' : `${response.count - 1} altres proves`;
                    show({ confirmation: true, dismissPlacement: 'header', title: 'Vols eliminar aquest usuari?',
                        message: `«${personName}» també és en ${otherProves}. Eliminar-lo el retira de tot l’esdeveniment; mantenir-lo només el treu d’aquesta caixa.`, actions: [
                        { label: 'Eliminar usuari', icon: 'event', destructive: true, run: () => { request.scope = 'event'; request.fingerprint = response.fingerprint; attempt(); } },
                        { label: 'No, mantenir usuari', icon: 'keep', run: () => { request.scope = 'box'; attempt(); } },
                    ] });
                } else {
                    reload(target);
                    show({ message: response.error || 'No s’ha pogut retirar el temporal.', actions: [{ label: 'Tornar-ho a provar', run: attempt }] });
                }
            });
        };
        attempt();
    };
    return { createTemporaryPerson, removeTemporaryPerson, notice, dismissNotice };
}
